import { cva, type VariantProps } from 'class-variance-authority'
import type React from 'react'

import { cn } from '@/lib/cn'

const badgeVariants = cva(
  'inline-flex items-center gap-2 rounded-2xl border px-2 py-1 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    variants: {
      variant: {
        default: 'text-system-grey-dark',
        success: 'text-system-content-grass-green',
        destructive: 'text-system-content-dark-orange',
        info: 'text-system-primary-blue',
      },
      mode: {
        filled: 'border-none',
        outline: 'border bg-transparent',
      },
    },
    compoundVariants: [
      {
        mode: 'filled',
        variant: 'default',
        className: 'bg-system-grey-dark/10',
      },
      {
        mode: 'filled',
        variant: 'success',
        className: 'bg-system-content-grass-green/10',
      },
      {
        mode: 'filled',
        variant: 'destructive',
        className: 'bg-system-content-dark-orange/10',
      },
      {
        mode: 'filled',
        variant: 'info',
        className: 'bg-system-primary-blue/10',
      },
      {
        mode: 'outline',
        variant: 'default',
        className: 'border-system-grey-dark',
      },
      {
        mode: 'outline',
        variant: 'success',
        className: 'border-system-content-grass-green',
      },
      {
        mode: 'outline',
        variant: 'destructive',
        className: 'border-system-content-dark-orange',
      },
      {
        mode: 'outline',
        variant: 'info',
        className: 'border-system-primary-blue',
      },
    ],
    defaultVariants: {
      variant: 'default',
      mode: 'filled',
    },
  },
)

const pointVariants = cva('size-2 rounded-full', {
  variants: {
    variant: {
      default: 'bg-system-grey-dark',
      success: 'bg-system-content-grass-green',
      destructive: 'bg-system-content-dark-orange',
      info: 'bg-system-primary-blue',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

export type BadgeProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof badgeVariants> & {
    withPoint?: boolean
    pointColor?: string
  }
const Badge = ({
  className,
  variant,
  mode,
  withPoint = false,
  pointColor,
  children,
  ...props
}: BadgeProps) => {
  return (
    <div className={cn(badgeVariants({ variant, mode }), className)} {...props}>
      {withPoint && (
        <div
          className={cn(pointVariants({ variant }))}
          style={pointColor ? { backgroundColor: pointColor } : undefined}
        />
      )}
      {children}
    </div>
  )
}
Badge.displayName = 'Badge'

export { Badge, badgeVariants }
